'use client';

import { c as _c } from "react/compiler-runtime";
import { useState, useEffect, useCallback, Suspense } from 'react';
import { usePathname } from 'next/navigation';
import { useHoverIntent } from 'react-use-hoverintent';
import Link from 'next/link';
import Image from 'next/image';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faHiveCartLight } from '@awesome.me/kit-94e593bf23/icons/kit/custom';
import { faClockRotateLeft } from '@awesome.me/kit-94e593bf23/icons/sharp/light';
import { useAppContext } from '@/app/_lib/_context';
import { useCart } from '@/app/_lib/hooks';
import { decodeEntities } from '@/app/_lib/services/ClientUtil';
import Price from '@/app/_components/Price';
import { getRecentlyViewedProductIds } from '@/app/_lib/services/RecentlyViewedService';
import ContinueShoppingLink from '@/app/_components/ContinueShoppingLink';

// Priority: variant > option_product_photo > thumb_1 > product_photo
const getProductImagePath = product => {
  if (product?.variant?.file_name) {
    return product.variant.file_name;
  }
  if (product?.option_product_photo) {
    return `option_photos/${product.option_product_photo}`;
  }
  if (product?.thumb_1) {
    return `thumbs/${product.thumb_1}`;
  }
  return product?.product_photo || null;
};
const RecentlyViewedSection = t0 => {
  const $ = _c(16);
  const {
    products,
    onClose
  } = t0;
  if (!products || products.length === 0) {
    return null;
  }
  let t1;
  let t2;
  let t3;
  let t4;
  if ($[0] !== onClose || $[1] !== products) {
    const displayProducts = products.slice(0, 4);
    t3 = "border-t border-gray-5 p-4";
    if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
      t4 = <div className="flex items-center gap-2 mb-3"><FontAwesomeIcon icon={faClockRotateLeft} className="text-xs text-light" /><span className="text-xs text-light font-medium">Recently Viewed</span></div>;
      $[6] = t4;
    } else {
      t4 = $[6];
    }
    t1 = "grid grid-cols-4 gap-2";
    let t5;
    if ($[7] !== onClose) {
      t5 = product => <Link key={product.sub_product_id} href={product.url || "#"} onClick={onClose} className="group"><div className="aspect-square bg-gray-2 rounded overflow-hidden">{getProductImagePath(product) ? <Image src={`${process.env.NEXT_PUBLIC_RESOURCE_URL}/${getProductImagePath(product)}`} alt={decodeEntities(product.sub_product_name) || "Product"} width={64} height={64} className="w-full h-full object-contain group-hover:scale-105 transition-transform" /> : <div className="w-full h-full flex items-center justify-center"><FontAwesomeIcon icon={faHiveCartLight} className="text-gray-10 text-sm" /></div>}</div></Link>;
      $[7] = onClose;
      $[8] = t5;
    } else {
      t5 = $[8];
    }
    t2 = displayProducts.map(t5);
    $[0] = onClose;
    $[1] = products;
    $[2] = t1;
    $[3] = t2;
    $[4] = t3;
    $[5] = t4;
  } else {
    t1 = $[2];
    t2 = $[3];
    t3 = $[4];
    t4 = $[5];
  }
  let t5;
  if ($[9] !== t1 || $[10] !== t2) {
    t5 = <div className={t1}>{t2}</div>;
    $[9] = t1;
    $[10] = t2;
    $[11] = t5;
  } else {
    t5 = $[11];
  }
  let t6;
  if ($[12] !== t3 || $[13] !== t4 || $[14] !== t5) {
    t6 = <div className={t3}>{t4}{t5}</div>;
    $[12] = t3;
    $[13] = t4;
    $[14] = t5;
    $[15] = t6;
  } else {
    t6 = $[15];
  }
  return t6;
};
const CartPreview = t0 => {
  const $ = _c(38);
  const {
    cart,
    recentlyViewed,
    onClose
  } = t0;
  if (!cart || !cart.cartItems || cart.cartItems.length === 0) {
    let t1;
    if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
      t1 = <span className="text-light text-sm">Your cart is empty</span>;
      $[0] = t1;
    } else {
      t1 = $[0];
    }
    let t2;
    if ($[1] !== onClose) {
      t2 = <div className="p-4 flex items-center justify-between">{t1}<ContinueShoppingLink className="text-primary text-sm hover:underline" onClick={onClose} /></div>;
      $[1] = onClose;
      $[2] = t2;
    } else {
      t2 = $[2];
    }
    let t3;
    if ($[3] !== onClose || $[4] !== recentlyViewed) {
      t3 = <RecentlyViewedSection products={recentlyViewed} onClose={onClose} />;
      $[3] = onClose;
      $[4] = recentlyViewed;
      $[5] = t3;
    } else {
      t3 = $[5];
    }
    let t4;
    if ($[6] !== t2 || $[7] !== t3) {
      t4 = <div className="w-[320px]">{t2}{t3}</div>;
      $[6] = t2;
      $[7] = t3;
      $[8] = t4;
    } else {
      t4 = $[8];
    }
    return t4;
  }
  const displayItems = cart.cartItems;
  let t1;
  if ($[9] !== displayItems || $[10] !== onClose) {
    let t2;
    if ($[12] !== onClose) {
      t2 = (item, index) => {
        const product = item.product || {};
        const imagePath = getProductImagePath(product);
        const imageSrc = imagePath ? `${process.env.NEXT_PUBLIC_RESOURCE_URL}/${imagePath}` : null;
        return <Link key={item.sku || index} href={product.url || "#"} onClick={onClose} className="group flex items-start gap-4 p-4 border-b border-gray-5 last:border-b-0 hover:bg-image-overlay transition-colors"><div className="w-16 h-16 flex-shrink-0 flex items-center justify-center overlay-block bg-white p-1">{imageSrc ? <Image src={imageSrc} alt={decodeEntities(product.sub_product_name) || "Product"} width={64} height={64} className="max-w-14 max-h-14 w-auto h-auto object-contain" /> : <div className="w-16 h-16 flex items-center justify-center bg-gray-2"><FontAwesomeIcon icon={faHiveCartLight} className="text-gray-10 text-xl" /></div>}</div><div className="flex-1 min-w-0 text-start"><span className="text-sm font-medium text-ink-dark line-clamp-2 leading-tight capitalize">{decodeEntities(product.sub_product_name) || "Product"}</span>{product.options && Object.keys(product.options).length > 0 && <div className="mt-1">{Object.entries(product.options).map(_temp)}</div>}{product.swatches && Object.keys(product.swatches).length > 0 && <div className="mt-1">{Object.entries(product.swatches).map(_temp2)}</div>}<div className="text-3xs mt-1"><span className="text-ink-light">Qty:</span> <span className="font-medium">{item.quantity}</span></div></div><div className="flex-shrink-0 text-right"><div className="text-sm font-medium"><Price amount={item.total_price} /></div>{item.original_total > item.total_price && <div className="line-through text-ink-light text-3xs"><Price amount={item.original_total} /></div>}{product.on_sale == 1 && product.discount_percentage > 0 && <div className="text-red text-3xs">{product.discount_percentage}% off</div>}</div></Link>;
      };
      $[12] = onClose;
      $[13] = t2;
    } else {
      t2 = $[13];
    }
    t1 = displayItems.map(t2);
    $[9] = displayItems;
    $[10] = onClose;
    $[11] = t1;
  } else {
    t1 = $[11];
  }
  let t2;
  if ($[14] !== t1) {
    t2 = <div className="max-h-[320px] overflow-y-auto">{t1}</div>;
    $[14] = t1;
    $[15] = t2;
  } else {
    t2 = $[15];
  }
  let t3;
  if ($[16] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = <span className="text-sm font-medium">Subtotal</span>;
    $[16] = t3;
  } else {
    t3 = $[16];
  }
  const t4 = cart.subTotal || 0;
  let t5;
  if ($[17] !== t4) {
    t5 = <div className="flex items-center justify-between mb-4">{t3}<Price amount={t4} className="text-sm" /></div>;
    $[17] = t4;
    $[18] = t5;
  } else {
    t5 = $[18];
  }
  let t6;
  if ($[19] !== cart.cartItems) {
    t6 = cart.cartItems.reduce(_temp3, 0);
    $[19] = cart.cartItems;
    $[20] = t6;
  } else {
    t6 = $[20];
  }
  const t7 = cart.cartItems.reduce(_temp4, 0) === 1 ? "item" : "items";
  let t8;
  if ($[21] !== onClose || $[22] !== t6 || $[23] !== t7) {
    t8 = <Link href="/cart" className="block w-full text-center bg-gray-5 hover:bg-primary text-ink hover:text-white text-sm font-medium py-2.5 px-4 rounded transition-colors" onClick={onClose}>View Cart ({t6} {t7})</Link>;
    $[21] = onClose;
    $[22] = t6;
    $[23] = t7;
    $[24] = t8;
  } else {
    t8 = $[24];
  }
  let t9;
  if ($[25] !== onClose) {
    t9 = <Link href="/checkout" className="block w-full text-center btn btn-primary text-sm font-medium py-2.5 px-4 mt-2" onClick={onClose}>Checkout</Link>;
    $[25] = onClose;
    $[26] = t9;
  } else {
    t9 = $[26];
  }
  let t10;
  if ($[27] !== t5 || $[28] !== t8 || $[29] !== t9) {
    t10 = <div className="p-4 border-t border-gray-5 bg-gray-1 last:rounded-b-lg">{t5}{t8}{t9}</div>;
    $[27] = t5;
    $[28] = t8;
    $[29] = t9;
    $[30] = t10;
  } else {
    t10 = $[30];
  }
  let t11;
  if ($[31] !== onClose || $[32] !== recentlyViewed) {
    t11 = <RecentlyViewedSection products={recentlyViewed} onClose={onClose} />;
    $[31] = onClose;
    $[32] = recentlyViewed;
    $[33] = t11;
  } else {
    t11 = $[33];
  }
  let t12;
  if ($[34] !== t10 || $[35] !== t11 || $[36] !== t2) {
    t12 = <div className="w-[320px]">{t2}{t10}{t11}</div>;
    $[34] = t10;
    $[35] = t11;
    $[36] = t2;
    $[37] = t12;
  } else {
    t12 = $[37];
  }
  return t12;
};
const HeaderCartIcon = t0 => {
  const $ = _c(33);
  const {
    className
  } = t0;
  const pathname = usePathname();
  const {
    quantity: cartQuantity,
    cart
  } = useCart();
  const {
    closeMobileMenu
  } = useAppContext();
  const isHomePage = pathname === "/";
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = [];
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const [recentlyViewed, setRecentlyViewed] = useState(t1);
  let t2;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = {
      sensitivity: 6,
      interval: 100,
      timeout: 300
    };
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const [isIntentHover, intentRef, setIsIntentHover] = useHoverIntent(t2);
  let t3;
  let t4;
  if ($[2] !== isIntentHover || $[3] !== recentlyViewed.length) {
    t3 = () => {
      if (isIntentHover && recentlyViewed.length === 0) {
        const fetchRecentlyViewed = async () => {
          const productIds = getRecentlyViewedProductIds().slice(0, 4);
          if (productIds.length === 0) {
            return;
          }
          const response = await fetch("/api/products/by-ids", {
            method: "POST",
            headers: {
              "Content-Type": "application/json"
            },
            body: JSON.stringify({
              sub_product_ids: productIds
            })
          }).then(_temp5).catch(_temp6);
          if (response.success && response.products) {
            setRecentlyViewed(response.products);
          }
        };
        fetchRecentlyViewed();
      }
    };
    t4 = [isIntentHover, recentlyViewed.length];
    $[2] = isIntentHover;
    $[3] = recentlyViewed.length;
    $[4] = t3;
    $[5] = t4;
  } else {
    t3 = $[4];
    t4 = $[5];
  }
  useEffect(t3, t4);
  let t5;
  if ($[6] !== closeMobileMenu || $[7] !== setIsIntentHover) {
    t5 = () => {
      closeMobileMenu();
      setIsIntentHover(false);
    };
    $[6] = closeMobileMenu;
    $[7] = setIsIntentHover;
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  const handleCartIconClick = t5;
  let t6;
  if ($[9] !== setIsIntentHover) {
    t6 = () => {
      setIsIntentHover(false);
    };
    $[9] = setIsIntentHover;
    $[10] = t6;
  } else {
    t6 = $[10];
  }
  const handleClosePreview = t6;
  const quantityClasses = isHomePage ? "text-white md:text-primary group-[.opaque]/header:text-primary" : "text-primary group-[.transparent]/header:text-white";
  const showPreview = isIntentHover && pathname !== "/cart";
  const t7 = `relative ${className || ""}`;
  const t8 = `absolute -top-[7px] left-[55%] -translate-x-1/2 text-center text-base md:text-2xs ${quantityClasses} ${!cartQuantity ? "hidden" : ""}`;
  let t9;
  if ($[11] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = <span className="sr-only text-dark">items in cart</span>;
    $[11] = t9;
  } else {
    t9 = $[11];
  }
  let t10;
  if ($[12] !== cartQuantity || $[13] !== t8) {
    t10 = <span aria-live="polite" aria-atomic="true" className={t8}>{cartQuantity}{t9}</span>;
    $[12] = cartQuantity;
    $[13] = t8;
    $[14] = t10;
  } else {
    t10 = $[14];
  }
  const t11 = `hover:text-primary text-[1.4rem] lg:text-nav-icon ${cartQuantity > 0 ? "text-primary-dep" : ""} group-[.transparent]/header:text-white`;
  let t12;
  if ($[15] !== t11) {
    t12 = <FontAwesomeIcon icon={faHiveCartLight} aria-hidden="true" className={t11} />;
    $[15] = t11;
    $[16] = t12;
  } else {
    t12 = $[16];
  }
  let t13;
  if ($[17] !== t10 || $[18] !== t12) {
    t13 = <Suspense>{t10}{t12}</Suspense>;
    $[17] = t10;
    $[18] = t12;
    $[19] = t13;
  } else {
    t13 = $[19];
  }
  let t14;
  if ($[20] !== handleCartIconClick || $[21] !== t13) {
    t14 = <Link href="/cart" aria-label="View shopping cart" onClick={handleCartIconClick} className="relative block">{t13}</Link>;
    $[20] = handleCartIconClick;
    $[21] = t13;
    $[22] = t14;
  } else {
    t14 = $[22];
  }
  let t15;
  if ($[23] !== cart || $[24] !== handleClosePreview || $[25] !== recentlyViewed || $[26] !== showPreview) {
    t15 = showPreview && <div className="hidden lg:block absolute top-full right-0 pt-4 z-[100]" role="dialog" aria-label="Cart preview"><div className="absolute top-2 right-1 w-0 h-0 border-l-8 border-r-8 border-b-8 border-l-transparent border-r-transparent border-b-white z-10" /><div className="bg-white rounded-lg shadow-lg border border-gray-5 -mr-3"><CartPreview cart={cart} recentlyViewed={recentlyViewed} onClose={handleClosePreview} /></div></div>;
    $[23] = cart;
    $[24] = handleClosePreview;
    $[25] = recentlyViewed;
    $[26] = showPreview;
    $[27] = t15;
  } else {
    t15 = $[27];
  }
  let t16;
  if ($[28] !== intentRef || $[29] !== t14 || $[30] !== t15 || $[31] !== t7) {
    t16 = <div ref={intentRef} className={t7}>{t14}{t15}</div>;
    $[28] = intentRef;
    $[29] = t14;
    $[30] = t15;
    $[31] = t7;
    $[32] = t16;
  } else {
    t16 = $[32];
  }
  return t16;
};
export default HeaderCartIcon;
function _temp(t0) {
  const [key, option] = t0;
  const hasUpcharge = option.option_price > 0 || option.original_option_price > 0;
  const isDiscounted = option.original_option_price > 0 && option.original_option_price > option.option_price;
  return <div key={key} className="text-3xs"><span className="text-ink-light capitalize">{option.selection_label}:</span> <span className="font-medium capitalize">{option.option_name}</span>{hasUpcharge && <span className="ml-1">{isDiscounted && <span className="line-through text-ink-light mr-1">+ <Price amount={option.original_option_price} /></span>}<span className="font-medium text-black">+ <Price amount={option.option_price} /></span></span>}</div>;
}
function _temp2(t0) {
  const [key_0, swatch] = t0;
  const swatchPrice = swatch.swatch_price || swatch.price || 0;
  const originalPrice = swatch.original_swatch_price || 0;
  const hasUpcharge_0 = swatchPrice > 0 || originalPrice > 0;
  const isDiscounted_0 = originalPrice > 0 && originalPrice > swatchPrice;
  return <div key={key_0} className="text-3xs"><span className="text-ink-light capitalize">{swatch.swatch_set_label}:</span> <span className="font-medium capitalize">{swatch.swatch_name}</span>{hasUpcharge_0 && <span className="ml-1">{isDiscounted_0 && <span className="line-through text-ink-light mr-1">+ <Price amount={originalPrice} /></span>}<span className="font-medium text-black">+ <Price amount={swatchPrice} /></span></span>}</div>;
}
function _temp3(sum, item_0) {
  return sum + (item_0.quantity || 1);
}
function _temp4(sum_0, item_1) {
  return sum_0 + (item_1.quantity || 1);
}
function _temp5(res) {
  return res.json();
}
function _temp6() {
  return {
    success: false
  };
}